import { BREWOUT_PAYS_REASONS, CUSTOMER_PAYS_REASONS } from '../components/ReturnCostDecisionDialog'

/**
 * returnCostProvenance — die HERKUNFT der Kostenträgerschaft, aus der Zeile.
 *
 * @description
 *   Bis 2026-09-09 stand die einzige Herkunftsangabe der Abzugszeile als feste
 *   Zeichenkette in `MoneyGuardsCard` (`"customer pays return shipping ·
 *   voluntary return"`) und las die Zeile nie. An prod gemessen, über die 128
 *   Zeilen mit Abzugsanzeige: 10 tragen `withdrawal` (der Text nannte den
 *   FALSCHEN Grund), 114 tragen gar keine Herkunft (der Text behauptete eine),
 *   4 tragen `unknown_default` („niemand hat entschieden" — der Text behauptete
 *   dort eine Entscheidung), und **0** tragen `voluntary_return`. Die Menge,
 *   für die der Satz zutraf, war leer.
 *
 *   🔴 **Dieses Modul nennt `is_voluntary_return` NICHT, und das ist eine
 *   Zusicherung, kein Zufall.** Wer zahlt, ist die GELDACHSE; sie hat im
 *   Backend genau eine Lesart (`kunde_traegt_rueckporto*`), und
 *   `scripts/check_rueckporto_lesarten.py` sagt in seinem eigenen Kopf, dass er
 *   das Frontend **nicht** abdeckt — dort liegen bereits acht Nicht-Test-Dateien
 *   mit teils gegenläufigen Lesarten. Eine neunte entsteht hier nicht. Dieses
 *   Modul beantwortet ausschliesslich die zweite Frage: WORAUS ergibt es sich.
 *   Gepinnt von `returnCostProvenance.test.js` § „die Geldachse bleibt draussen".
 *
 *   🔴 Die Beschriftungen sind GELIEHEN, nicht abgetippt: sie kommen aus den
 *   Listen, die `ReturnCostDecisionDialog` ohnehin exportiert. Damit können der
 *   Text, den ein Bearbeiter im Dialog WÄHLT, und der Text, den er danach an der
 *   Zeile LIEST, nicht auseinanderlaufen.
 *
 * @useWhen
 *   - Eine Geldfläche zeigt einen Rückporto-Abzug und soll sagen, worauf er
 *     sich stützt
 *   - Eine Retouren-Fläche soll den Unterschied zwischen „entschieden",
 *     „Rückfall" und „nichts vermerkt" sichtbar machen
 *
 * @dontUseFor
 *   - Die Frage „WER ZAHLT" — dafür gibt es `kunde_traegt_rueckporto` im
 *     Backend und im Frontend die Zeile, an der die Fläche ohnehin schon
 *     entscheidet (in `MoneyGuardsCard` ist das `deductionApplies`). Dieses
 *     Modul hat dazu bewusst nichts zu sagen.
 *   - Die Herkunft der RETOUREN-AKTION (Modell oder Regel) — das ist
 *     `rma/lib/returnActionProvenance.js`, dieselbe Verzeichnis-Nachbarschaft
 *     und eine ANDERE Achse: dort geht es darum, wer die Handlung
 *     vorgeschlagen hat, hier darum, wer die Kosten trägt.
 *   - Ein generisches Herkunfts-Abzeichen an beliebigen Flächen — dafür gibt es
 *     `components/common/provenance.js` (`ProvenanceMark`, `PROVENANCE`). Es
 *     trägt Herkunfts-ARTEN (gemessen/geschätzt/übernommen), keine
 *     fachlichen Gründe; dieses Modul liefert den TEXT, nicht das Abzeichen.
 *   - Als Vorprüfung vor einem `PUT`. Der 422 des Servers ist die Instanz.
 *
 * @related
 *   - ReturnCostDecisionDialog — die Quelle der Beschriftungen
 *   - returnActionProvenance — Nachbarmodul, andere Achse (siehe @dontUseFor)
 *   - MoneyGuardsCard — der erste Leser
 */

// Beschriftungen aus dem Dialog — Wert -> Klartext.
//
// 🔴 **Wer hier ein Wort aendert, aendert die HOEHE von zehn prod-Zeilen.**
// Gate 16 hat am 2026-09-09 im Browser gemessen (echtes Theme, echte
// Inter-Webschrift, echte Seitenleisten-Geometrie, Kontrastkette kalibriert):
// in der 286-px-Spalte der Geldkarte misst
//
//     `customer pays return shipping · Withdrawal (Widerruf)`  =  281,31 px
//
// also **4,69 px Luft**. Jede laengere Beschriftung, jedes laengere
// Zahler-Literal und jeder Schriftrueckfall kippt genau die 10 Widerruf-Zeilen
// in eine zweite Zeile (+17,25 px). 124 von 128 prod-Abzugszeilen wachsen heute
// um 0 px; die 4 `unknown_default`-Zeilen um 17,25 px.
const REASON_LABELS = Object.fromEntries(
  [...BREWOUT_PAYS_REASONS, ...CUSTOMER_PAYS_REASONS].map(o => [o.value, o.label]),
)

// 🔴 `unknown_default` steht in KEINER der beiden Dialoglisten, und das ist
// Absicht (`ReturnCostDecisionDialog` Kopfblock): der Dialog bietet ihn nicht
// an, weil ein Mensch, der ihn öffnet, gerade entscheidet. ANZUZEIGEN ist er
// trotzdem — 4 Zeilen an prod tragen ihn, und sie sind Abzugszeilen. Er darf
// nicht mit „nichts vermerkt" zusammenfallen: die Zeile sagt ausdrücklich
// „niemand hat entschieden, im Zweifel zahlt der Kunde" (Owner B2, 31.08.),
// und das ist eine Aussage, keine Lücke.
// Er gehört hierher und NICHT in `CUSTOMER_PAYS_REASONS` — diese Liste ist die
// Auswahlliste des Dialogs, und der Server weist den Wert über den Kanal ab.
export const UNKNOWN_DEFAULT_LABEL = 'no decision recorded — system default'

// N81 (Owner-Entscheid E5/2026-09-28): `resolution_without_refund` ist die
// Ableitung aus der LÖSUNG. Auf Ersatz, Ersatzteil, Gutschein und keiner Maßnahme
// trägt Brewout das Rückporto, weil es keine Erstattung gibt, von der es
// abgezogen werden könnte. Wie `unknown_default` steht er in KEINER Dialogliste
// (Plan D11): er entsteht nur automatisch, der Server reserviert ihn im Kanal,
// und ein Mensch, der den Dialog öffnet, entscheidet gerade selbst. Anzuzeigen
// ist er trotzdem, sonst stünde die rohe Kennung auf dem Schirm.
//
// 🔴 Der TEXT nennt seit dem Regel-16-Gate (W5, 2026-09-29) die URSACHE, wie die
// drei anderen Brewout-Gründe — vorher nannte er den ZAHLER („carried by us, no
// refund to deduct from"). Der Zahler steht im Feld daneben
// (`return_cost_decided_by`), und die Kachel sagt ihn ohnehin („Brewout pays
// return shipping"). Zweimal derselbe Name für eine Tatsache, und die Ursache
// fiel unter den Tisch. Die Breite ist unkritisch: 26 Zeichen gegen 45 des
// längsten Grundes.
export const RESOLUTION_WITHOUT_REFUND_LABEL = 'no refund on this resolution'

// Was dasteht, wenn die Zeile gar nichts trägt. 114 von 128 Zeilen an prod.
// 🔴 Kein erfundener Vorgabetext. Eine Zeile ohne Herkunft IST ein Messwert —
// genau der, den die leeren Spalten sichtbar machen sollen (Owner § E4).
export const NO_PROVENANCE_LABEL = 'reason not recorded'

// Nur die drei ECHTEN Entscheider aus `RETURN_COST_DECIDERS`. `system_default`
// fehlt bewusst: er ist die Signatur der Ableitung, nicht die eines
// Entscheiders — er sagt „keiner der drei § E2-Wege war beteiligt". Ihn als
// Entscheider zu nennen wäre dieselbe unbelegte Behauptung, die dieses Modul
// abschafft. Was in seinem Fall zu sagen ist, sagt der GRUND daneben.
//
// ⚠️ Warum der Entscheider überhaupt angezeigt wird, obwohl er heute an
// **0 von 255** Fällen an prod gesetzt ist (nur `system_default` 14× und
// `NULL` 241×): Owner § E2 macht den benannten Entscheider zur BEDINGUNG einer
// kostenfreien Retoure („Nachweis UND Bestätigung"). Wer prüfen will, ob die
// Bedingung erfüllt war, braucht genau dieses Feld — und solange es leer ist,
// kostet die Anzeige nichts, weil sie dann gar nicht erscheint. Sie wird
// sichtbar, sobald der § E2-Weg zum ersten Mal benutzt wird.
//
// ⚠️ Und wenn `bei_samuel` (4) der Story mit „ja, Migration 249" beantwortet
// wird — ein eigener Wert für „das Gesetz hat entschieden" —, kostet das hier
// genau EINEN Eintrag in dieser Tabelle.
//
// Exportiert seit dem 2026-09-26 für die Audit-Zeile `return_cost_change`
// (`rma/timeline/toActivityEntry.js`, Regel-16-Tor W3 zu E10): dort stand der
// Entscheider bis dahin als roher Schlüssel. Geliehen, nicht abgetippt, damit
// Geldkarte und Audit dasselbe Wort für denselben Entscheider sagen.
export const DECIDER_LABELS = {
  staff: 'decided by staff',
  agent: 'decided by the agent',
  customer_email: 'from the customer email',
}

/**
 * Der Grund, als Klartext — oder die ehrliche Auskunft, dass keiner dasteht.
 */
export function returnCostReasonLabel(reason) {
  if (!reason) return NO_PROVENANCE_LABEL
  if (reason === 'unknown_default') return UNKNOWN_DEFAULT_LABEL
  if (reason === 'resolution_without_refund') return RESOLUTION_WITHOUT_REFUND_LABEL
  // Ein Grund, den weder der Dialog noch der Rückfall kennt, ist ein Drift
  // zwischen `backend/rma/constants.py` und dieser Oberfläche. Ihn roh zu
  // zeigen ist besser als ihn zu verschlucken: er wird dann gemeldet, statt
  // still als „keine Herkunft" durchzugehen.
  return REASON_LABELS[reason] || reason
}

/**
 * Die Herkunftsangabe für eine Fall-Nutzlast — Grund, und wenn es einen gibt,
 * der Entscheider. **Ohne** die Aussage, wer zahlt (siehe @dontUseFor).
 *
 * @param caseData — gelesen werden `return_cost_reason`, `return_cost_decided_by`
 * @returns string — z.B.
 *   `Withdrawal (Widerruf)`
 *   `Voluntary return · decided by staff`
 *   `reason not recorded`
 */
export function returnCostProvenance(caseData) {
  const c = caseData || {}
  const teile = [returnCostReasonLabel(c.return_cost_reason)]
  // Der Entscheider kommt nur dazu, wenn es einen gibt. Bei `system_default`
  // und bei `null` trägt der Grund die ganze Auskunft — ein angehängtes
  // „decided by nobody" wäre Lärm, kein Signal.
  const wer = DECIDER_LABELS[c.return_cost_decided_by]
  if (wer) teile.push(wer)
  return teile.join(' · ')
}

export default returnCostProvenance
